import React from 'react';
import type { LayerRenderer } from '../../../types';
import { buildSymbolSvg, getPointSymbol } from '../../../layers/symbols';
import PointSymbolGallery from './PointSymbolGallery';
import { PropertySection, SliderField } from './SymbolProperties';

/**
 * POINT symbology only.
 *
 * The predefined symbol gallery and point-only properties (size, rotation)
 * live exclusively here, so they can never be rendered for a line or polygon
 * layer. SymbologyPanel mounts this component only when the resolved geometry
 * type is 'point'.
 */
export default function PointSymbologyPanel({
  renderer,
  onChange,
  sldOnly = false,
}: {
  renderer: LayerRenderer;
  onChange: (changes: Partial<LayerRenderer>) => void;
  /** GeoServer renders WMS itself and only understands SLD marks. */
  sldOnly?: boolean;
}) {
  const symbol = getPointSymbol(renderer.symbolId);

  return (
    <>
      <PointSymbolGallery renderer={renderer} onChange={onChange} sldOnly={sldOnly} />

      <PropertySection title="Preview">
        <div
          className="flex h-20 items-center justify-center rounded-lg border border-slate-200 bg-slate-50"
          dangerouslySetInnerHTML={{
            __html: buildSymbolSvg({
              path: symbol.path,
              size: Math.min(renderer.pointSize * 2, 56),
              color: renderer.color,
              outlineColor: renderer.outlineColor,
              outlineWidth: renderer.outlineWidth,
              opacity: renderer.fillOpacity,
              rotation: renderer.rotation ?? 0,
            }),
          }}
        />
      </PropertySection>

      {/* GeoServer renders WMS server-side with no clustering concept of
          its own — this only ever means anything for the browser-drawn
          (ArcGIS FeatureServer / local file) point layers `sldOnly` is
          false for. */}
      {!sldOnly && (
        <PropertySection title="Clustering">
          <label className="flex items-center justify-between py-1.5">
            <span className="text-xs text-slate-600">Group nearby points</span>
            <input
              type="checkbox"
              checked={renderer.cluster ?? false}
              onChange={(event) => onChange({ cluster: event.target.checked })}
              className="h-4 w-4 accent-blue-600"
            />
          </label>
          {renderer.cluster && (
            <>
              <SliderField
                label="Cluster distance"
                value={renderer.clusterDistance ?? 40}
                min={10}
                max={120}
                step={5}
                suffix="px"
                onChange={(value) => onChange({ clusterDistance: value })}
              />
              <ClusterMarkerStylePicker renderer={renderer} onChange={onChange} />
            </>
          )}
        </PropertySection>
      )}
    </>
  );
}

const CLUSTER_MARKER_STYLES: {
  id: NonNullable<LayerRenderer['clusterMarkerStyle']>;
  label: string;
}[] = [
  { id: 'solid', label: 'Solid' },
  { id: 'ring', label: 'Ring' },
  { id: 'graduated', label: 'By size' },
];

/** A tiny CSS-drawn preview of each cluster bubble style — not the map's own
 *  render path (that's clusterBubbleStyle in MapViewOL.tsx), just enough of
 *  a swatch to pick between "plain circle", "circle with a soft halo", and
 *  "halo coloured by how big the cluster is" without leaving the panel. */
function ClusterMarkerStylePicker({
  renderer,
  onChange,
}: {
  renderer: LayerRenderer;
  onChange: (changes: Partial<LayerRenderer>) => void;
}) {
  const selected = renderer.clusterMarkerStyle ?? 'solid';

  return (
    <div className="py-1.5">
      <span className="mb-1.5 block text-xs text-slate-600">Cluster marker</span>
      <div className="flex gap-2">
        {CLUSTER_MARKER_STYLES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={selected === entry.id}
            onClick={() => onChange({ clusterMarkerStyle: entry.id })}
            className={`flex flex-1 flex-col items-center gap-1 rounded-lg border px-2 py-2 transition ${
              selected === entry.id
                ? 'border-blue-400 bg-blue-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <ClusterMarkerSwatch style={entry.id} color={renderer.color} />
            <span className="text-[10px] font-medium text-slate-600">{entry.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ClusterMarkerSwatch({
  style,
  color,
}: {
  style: NonNullable<LayerRenderer['clusterMarkerStyle']>;
  color: string;
}) {
  if (style === 'solid') {
    return (
      <span
        aria-hidden="true"
        className="flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold text-white"
        style={{ backgroundColor: color }}
      >
        9
      </span>
    );
  }
  if (style === 'ring') {
    return (
      <span
        aria-hidden="true"
        className="flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold text-white"
        style={{ backgroundColor: color, boxShadow: `0 0 0 4px ${color}55` }}
      >
        9
      </span>
    );
  }
  // 'graduated' — three size buckets, each its own colour, same halo look.
  return (
    <span className="flex items-center gap-0.5" aria-hidden="true">
      <span className="h-3 w-3 rounded-full bg-amber-400" style={{ boxShadow: '0 0 0 2px #FBBF2455' }} />
      <span className="h-4 w-4 rounded-full bg-orange-500" style={{ boxShadow: '0 0 0 2px #F9731655' }} />
      <span className="h-5 w-5 rounded-full bg-red-600" style={{ boxShadow: '0 0 0 2px #DC262655' }} />
    </span>
  );
}
